import { __ } from '@wordpress/i18n';
import {
	Button,
	Panel,
	PanelBody,
	PanelRow,
	__experimentalHeading as Heading,
	TextControl,
	SelectControl,
	ToggleControl,
	ColorPicker,
} from '@wordpress/components';
import { useSettings, useResponsive } from '../hooks';
import { Notices } from './notices';
import { useState, useEffect } from '@wordpress/element';
import { useSelect } from '@wordpress/data';
// import { store as noticesStore } from '@wordpress/notices';
import { store as coreDataStore } from '@wordpress/core-data';
import { validateUrl, validateClientSecret } from '../utils/validators';
import { PageSelector } from './page-selector';
import {
	getFilteredPages,
	handlePageSelect,
	getPageForLanguage,
	getLinkForLanguagePage,
	getTitleForLanguagePage,
} from '../utils/language-utils';
// import { MessageControl } from './controls';

/*
const SettingsTitle = () => {
	return (
		<Heading level={ 1 }>
			{ __( 'Linear Announcement Bar', 'linear' ) }
		</Heading>
	);
};
*/

const SaveButton = ( { onClick, disabled } ) => {
	return (
		<Button
			variant="primary"
			onClick={ onClick }
			__next40pxDefaultSize
			disabled={ disabled }
		>
			{ __( 'Save', 'linear' ) }
		</Button>
	);
};

const SettingsPage = () => {
	const {
		appUrl,
		setAppUrl,
		dataUrl,
		setDataUrl,
		clientSecret,
		setClientSecret,
		dynamicParentPages,
		setDynamicParentPages,
		renderingMethod,
		setRenderingMethod,
		shortcodeRenderingMethod,
		setShortcodeRenderingMethod,
		useTestData,
		setUseTestData,
		listingLeadsIntegration,
		setListingLeadsIntegration,
		featuredImage,
		setFeaturedImage,
		themeColor,
		setThemeColor,
		resultsPerPage,
		setResultsPerPage,
		customCss,
		setCustomCss,
		customCssSrc,
		setCustomCssSrc,
		additionalSettings,
		setAdditionalSettings,
		saveSettings,
	} = useSettings();

	const [ isSaving, setIsSaving ] = useState( false );
	const [ detectedLanguages, setDetectedLanguages ] = useState( [ 'fi' ] );
	const { isMobile } = useResponsive();
	const exceptions = window.linear_admin_obj.exception_rules || [];

	// Fetch available languages from the REST API
	useEffect( () => {
		const fetchLanguages = async () => {
			try {
				const response = await fetch( '/wp-json/linear/v3/languages', {
					headers: {
						'X-WP-Nonce': linear_admin_obj.rest.nonce,
					},
				} );

				if ( ! response.ok ) {
					throw new Error( 'Failed to fetch languages' );
				}

				const languages = await response.json();
				setDetectedLanguages( languages );
			} catch ( error ) {
				console.error( 'Error fetching languages:', error );
				const languages = [
					document
						.getElementsByTagName( 'html' )[ 0 ]
						.getAttribute( 'lang' ) || 'fi',
				];
				setDetectedLanguages( languages );
			}
		};

		fetchLanguages();
	}, [] );

	useEffect( () => {
		const cleanedPages = {};

		detectedLanguages.forEach( ( lang ) => {
			if ( dynamicParentPages[ lang ] ) {
				cleanedPages[ lang ] = dynamicParentPages[ lang ];
			}
		} );

		if (JSON.stringify(cleanedPages) !== JSON.stringify(dynamicParentPages)) {
			setDynamicParentPages(cleanedPages);
		}
	}, [detectedLanguages, dynamicParentPages]);

	const handleSaveSettings = () => {
		if ( isSaving ) {
			return;
		}

		setIsSaving( true );
		saveSettings();

		window.scrollTo({
			top: 0,
			behavior: 'smooth',
		});

		// Stop save spamming
		setTimeout( () => {
			setIsSaving( false );
		}, 3000 );
	};

	const pages = useSelect( ( select ) => {
		const query = {
			per_page: -1,
			status: 'publish',
		};
		const fetchedPages = select( coreDataStore ).getEntityRecords(
			'postType',
			'page',
			query
		);
		return fetchedPages;
	}, [] );

	const languageSelectorStyles = {
		languageSection: {
			marginBottom: '20px',
			padding: '15px',
			backgroundColor: '#f9f9f9',
			borderRadius: '4px',
			border: '1px solid #e0e0e0',
		},
		languageTitle: {
			marginTop: '0',
			marginBottom: '10px',
			fontSize: '14px',
			fontWeight: 'bold',
			textTransform: 'capitalize',
		},
	};

	const styleTextControl = {
		padding: '10px',
		fontSize: '16px',
	};

	const styleTextControlError = {
		borderColor: 'red',
		backgroundColor: '#ffe6e6',
	};

	const styleRenderMethodContainer = {
		display: 'flex',
		flexDirection: isMobile ? 'column' : 'row',
		gap: isMobile ? '1rem' : '2rem',
		width: '100%',
	};

	const styleRenderMethodItem = {
		flex: isMobile ? '1 1 100%' : '1',
		minWidth: isMobile ? '100%' : '0',
	};

	const openMediaLibrary = () => {
		const mediaFrame = wp.media({
		  title: 'Select or Upload Media',
		  button: { text: 'Use this media' },
		  multiple: false
		});
		
		mediaFrame.on('select', function() {
		  const attachment = mediaFrame.state().get('selection').first().toJSON();
		  setFeaturedImage(attachment.url);
		});
		
		mediaFrame.open();
	};

	let firstDynamicParentPage = '#';

	if( detectedLanguages.length > 0 ) {
		const firstLanguage = detectedLanguages[ 0 ];
		firstDynamicParentPage = getLinkForLanguagePage(
			dynamicParentPages,
			pages,
			detectedLanguages,
			firstLanguage
		) + '?guide';
	}

	return (
		<>
			<Notices />
			<Panel>
				<PanelBody title={ __( 'Setup', 'linear' ) }>
					<PanelRow>
						<blockquote>
							<div style={ { flex: '1' } }>
								<Heading level={4}>{__('App URL', 'linear')}</Heading>
								<br/>
								<TextControl
									value={ appUrl }
									onChange={ ( value ) => setAppUrl( value ) }
									style={ {
										...styleTextControl,
										...( appUrl !== '' &&
										! validateUrl( appUrl )
											? styleTextControlError
											: {} ),
									} }
									placeholder={ __( 'https://...', 'linear' ) }
									help={ __(
										'A source for listings view',
										'linear'
									) }
								/>
							</div>
						</blockquote>
					</PanelRow>

					<PanelRow>
						<blockquote>
							<div style={ { flex: '1' } }>
								<Heading level={4}>{__('Data source URL', 'linear')}</Heading>
								<br/>
								<TextControl
									value={ dataUrl }
									onChange={ ( value ) => setDataUrl( value ) }
									style={ {
										...styleTextControl,
										...( dataUrl !== '' &&
										! validateUrl( dataUrl )
											? styleTextControlError
											: {} ),
									} }
									placeholder={ __( 'https://...', 'linear' ) }
									help={ __( 'Our main data-source', 'linear' ) }
								/>
							</div>
						</blockquote>
					</PanelRow>

					<PanelRow>
						<blockquote>
							<div style={ { flex: '1' } }>
								<Heading level={4}>{__('Client secret', 'linear')}</Heading>
								<br/>
								<TextControl
									value={ clientSecret }
									onChange={ ( value ) =>
										setClientSecret( value )
									}
									style={ {
										...styleTextControl,
										...( clientSecret !== '' &&
										! validateClientSecret( clientSecret )
											? styleTextControlError
											: {} ),
									} }
									placeholder="..."
									help={ __(
										'Linear should provide this for you',
										'linear'
									) }
								/>
							</div>
						</blockquote>
					</PanelRow>

					{ /* Show language-specific parent page selectors */ }
					<PanelRow>
						<blockquote>
							<div>
								<Heading level={4}>{__('Dynamic Parent Pages', 'linear')}</Heading>
								<br/>
								<div style={{ marginLeft: '0rem' }}>
									<div style={ { marginBottom: '15px' } }>
										<p>
											{ __(
												'We have to set a dynamic parent page for each language. This is the page that will be used to load the listings dynamically. You might want to create a page dedicated for this purpose, if you haven\'t already done that.',
												'linear'
											) }
										</p>
										{ exceptions.includes('translatePress') && (
											<p style={{fontWeight: 'bold', textDecoration: 'underline'}}>
												{ __(
													'If you are using TranslatePress, you can only have one set dynamic parent page, but the content will be determed by the TranslatePress selected language.',
													'linear'
												) }
											</p>
										)}
									</div>

									{ detectedLanguages.map( ( lang ) => (
										<div
											key={ lang }
											style={ languageSelectorStyles.languageSection }
										>
											<h3
												style={
													languageSelectorStyles.languageTitle
												}
											>
												{ lang }
											</h3>
											<PageSelector
												label={
													__(
														'Select any page to use as parent for',
														'linear'
													) +
													' ' +
													lang
												}
												value={ getPageForLanguage(
													dynamicParentPages,
													detectedLanguages,
													lang
												) }
												pages={ getFilteredPages(
													pages,
													detectedLanguages,
													lang
												) }
												onChange={ ( value ) =>
													setDynamicParentPages(
														handlePageSelect(
															value,
															dynamicParentPages,
															detectedLanguages,
															lang,
															exceptions
														)
													)
												}
												getLinkForLanguagePage={ () =>
													getLinkForLanguagePage(
														dynamicParentPages,
														pages,
														detectedLanguages,
														lang
													)
												}
												getTitleForLanguagePage={ () =>
													getTitleForLanguagePage(
														dynamicParentPages,
														pages,
														detectedLanguages,
														lang
													)
												}
											/>
										</div>
									) ) }

									<div
										style={ {
											marginTop: '15px',
											fontSize: '13px',
											fontStyle: 'italic',
										} }
									>
										{ __(
											'Changing parent pages will flush rewrite rules to ensure proper URL handling.',
											'linear'
										) }
									</div>
								</div>
							</div>
						</blockquote>
					</PanelRow>

					<PanelRow>
						<blockquote>
							<div>
								<Heading level={4}>{__('Rendering method', 'linear')}</Heading>
								<br/>
								<div style={ styleRenderMethodContainer }>
									<div style={ styleRenderMethodItem }>
										<SelectControl
											value={ renderingMethod }
											options={ [
												{
													label: __('Inline (default)','linear'),
													value: 'inline',
												},
												{
													label: __( 'Shortcode', 'linear' ),
													value: 'shortcode',
												},
												{
													label: __( 'Gutenberg block', 'linear' ),
													value: 'gutenberg',
												},
											] }
											onChange={ ( value ) =>
												setRenderingMethod( value )
											}
											help={ __(
												'Decide on how you wish the content to be shown. If you have a custom WordPress solution, you might be better off by using the Shortcode option.',
												'linear'
											) }
											style={ { flex: '1' } }
										/>
									</div>
									<div style={ styleRenderMethodItem }>
										{ renderingMethod === 'inline' ? (
											<p>
												{ __(
													'The content will be rendered inline after the page main content.',
													'linear'
												) }
											</p>
										) : ''}
										{ renderingMethod === 'shortcode' ? (
											<div>
												<p>
													{ __(
														'The dynamic content will be rendered via a shortcode. You can place the shortcode anywhere on your selected page. That page will be used to dynamically load Linear listings.',
														'linear'
													) }
												</p>
												<p>
													{ __(
														'The shortcode is:',
														'linear'
													) }{ ' ' }
													<code>
														[linear_listings_content]
													</code>
												</p>
											</div>
										) : ''}
										{ renderingMethod === 'gutenberg' ? (
											<div>
												<p>
													{ detectedLanguages.length > 1 ? __(
														'Please add the Gutenberg block "Linear dynamic listings" to the pages: ',
														'linear'
													) : __(
														'Please add the Gutenberg block "Linear dynamic listings" to the page: ',
														'linear'
													)}
												</p>
												<div style={{display: 'flex', flexDirection: 'column'}}>
													{ dynamicParentPages && Object.entries(dynamicParentPages)
														.reduce((acc, [lang, id]) => {
															if (!acc.some(([_, existingId]) => existingId === id)) {
																acc.push([lang, id]);
															}
															return acc;
														}, [])
														.map(([lang, id]) => (
															<a
																key={id}
																href={getLinkForLanguagePage(
																	dynamicParentPages,
																	pages,
																	detectedLanguages,
																	lang
																)}
															>
																{getTitleForLanguagePage(
																	dynamicParentPages,
																	pages,
																	detectedLanguages,
																	lang
																)}
															</a>
														))
													}
												</div>
											</div>
										) : ''}
									</div>
									{ renderingMethod === 'shortcode' ? (
										<div style={ styleRenderMethodItem }>
											<SelectControl
												label={ __(
													'Shortcode rendering method',
													'linear'
												) }
												value={ shortcodeRenderingMethod }
												options={ [
													{
														label: __(
															'Should render both single listings and a list of listings',
															'linear'
														),
														value: 'default',
													},
													{
														label: __(
															'Should only render single listings',
															'linear'
														),
														value: 'single',
													},
												] }
												onChange={ ( value ) =>
													setShortcodeRenderingMethod( value )
												}
												help={ __(
													'By default, we render both single listings and the list of listings in the same place. If you want to render only single listings, and provide the list listings via e.g. a shortcode or block, you can adjust this option.',
													'linear'
												) }
												style={ { flex: '1' } }
											/>
										</div>
									) : (
										''
									) }
								</div>
							</div>
						</blockquote>
					</PanelRow>
					<PanelRow>
						<blockquote>
							<div style={ {} }>
								<Heading level={4}>{__('Use test data', 'linear')}</Heading>
								<br/>
								<ToggleControl
									checked={ useTestData }
									onChange={ () =>
										setUseTestData(
											( state ) => ! state
										)
									}
									__nextHasNoMarginBottom={true}
									help={ __(
										'In case you are given credentials that are meant for testing purposes, you might need to enable this specific test-data mode',
										'linear'
									) }
								/>
							</div>
						</blockquote>
					</PanelRow>
				</PanelBody>

				<PanelBody title={ __( 'Global settings', 'linear' ) }>
					<p>{__('These settings are automatically applied to all listings, but can be overwritten on a per-listing basis', 'linear')}</p>
					<PanelRow>
						<blockquote>
							<div style={ {} }>
								<Heading level={4}>{__('Contact form integration', 'linear')}</Heading>
								<br/>
								<ToggleControl
									checked={ listingLeadsIntegration }
									onChange={ () =>
										setListingLeadsIntegration(
											( state ) => ! state
										)
									}
									__nextHasNoMarginBottom={true}
									help={ __(
										'In case you have Leads activates for your account, you can activate them for your listings with this toggle. If however not, please contact Linear and we will sort it out for you.',
										'linear'
									) }
								/>
							</div>
						</blockquote>
					</PanelRow>
					<PanelRow>
						<blockquote>
							<div style={ { flex: '1' } }>
								<Heading level={4}>{__('Default results per page', 'linear')}</Heading>
								<br/>
								<input
									type="number"
									value={ resultsPerPage }
									onChange={ ( event ) => setResultsPerPage( event.target.value ) }
									placeholder={ __( '4', 'linear' ) }
									style={{ padding: '8px', fontSize: '14px' }}
								/>
							</div>
						</blockquote>
					</PanelRow>
					<PanelRow>
						<blockquote>
							<div style={{ width: '100%' }}>
								<Heading level={4}>{__('Theme Color', 'linear')}</Heading>
								<p>
									{__('Select a theme color to be used across the plugin.', 'linear')}
								</p>
								<div style={{
									display: 'flex',
									gap: '0.5rem',
									alignItems: 'center',
									marginBottom: '1rem',
								}}>
									<span>{__('Selected Color:', 'linear')}</span>
									<span
										style={{
											display: 'inline-block',
											width: '20px',
											height: '20px',
											backgroundColor: themeColor,
											border: '1px solid #ccc',
											marginLeft: '5px',
											borderRadius: '100px',
										}}
									></span>
								</div>
								<ColorPicker
									color={themeColor}
									onChangeComplete={(color) => setThemeColor(color.hex)}
									disableAlpha
								/>
							</div>
						</blockquote>
                    </PanelRow>
					<PanelRow>
						<blockquote>
							<div>
								<Heading level={4}>{__('Featured image', 'linear')}</Heading>
								<p>
									{ __(
										'Select or upload an image to be used as the brand feature image. Most often this is the logo of the company.',
										'linear'
									) }
								</p>

								<Button variant="primary" onClick={openMediaLibrary}>{__('Select Image', 'linear')}</Button>

								{ featuredImage && (
									<div style={ { marginTop: '10px' } }>
										<img
											src={ featuredImage }
											alt={ __( 'Selected Image', 'linear' ) }
											style={ { maxWidth: '100%', maxHeight: '120px', height: 'auto', border: '1px solid #ccc' } }
										/><br/>
										<Button
											onClick={ () => setFeaturedImage('') }
											variant="link"
											isDestructive
											style={ { marginTop: '10px' } }
										>
											{ __( 'Remove Image', 'linear' ) }
										</Button>
									</div>
								) }
							</div>
						</blockquote>
					</PanelRow>
					<PanelRow>
						<blockquote>
							<div style={ { flex: '1' } }>
								<Heading level={4}>{__('Custom CSS', 'linear')}</Heading>
								<br/>
								<textarea
									value={ customCss }
									onChange={ ( event ) => setCustomCss( event.target.value ) }
									placeholder={ __( 'Enter your custom CSS here...', 'linear' ) }
									style={{ width: '100%', padding: '8px', fontSize: '14px', minHeight: '100px' }}
								/>
							</div>
						</blockquote>
					</PanelRow>
					<PanelRow>
						<blockquote>
							<div style={ { flex: '1' } }>
								<Heading level={4}>{__('Custom CSS source', 'linear')}</Heading>
								<br/>
								<input
									type="string"
									value={ customCssSrc }
									onChange={ ( event ) => setCustomCssSrc( event.target.value ) }
									placeholder={ window.location.origin + '/custom-styles.css' }
									style={{ width: '100%', padding: '8px', fontSize: '14px' }}
								/>
							</div>
						</blockquote>
					</PanelRow>
					<PanelRow>
						<blockquote>
							<div style={ { flex: '1' } }>
								<Heading level={4}>{__('Additional global settings', 'linear')}</Heading>
								<p>{__('You can define here other globally used settings. These can be overwritten on a per-listing basis, but are as default applied to all listings.', 'linear')}</p>
								{firstDynamicParentPage &&
									<Button
										variant="primary"
										href={firstDynamicParentPage}
										style={{ marginTop: '10px' }}
									>{__('See available attributes', 'linear')}</Button>
								}
								<br/>

								{/* Additional custom global settings */}
								{additionalSettings && Array.isArray(additionalSettings) && additionalSettings.length > 0 &&
									<div style={{ marginTop: '10px' }}>
										{additionalSettings.map((item, index) => (
											<div
												key={item.id || `setting-${index}`}
												style={{
													display: 'flex',
													alignItems: 'center',
													marginBottom: '10px',
													gap: '10px',
												}}
											>
												<TextControl
													label={__('Key', 'linear')}
													value={item.key || ''}
													onChange={(value) => {
														const updatedSettings = additionalSettings.map((setting, i) =>
															i === index ? { ...setting, key: value } : setting
														);
														setAdditionalSettings(updatedSettings);
													}}
													placeholder={__('Enter key', 'linear')}
													style={{ flex: '1' }}
												/>
												<TextControl
													label={__('Value', 'linear')}
													value={item.value || ''}
													onChange={(value) => {
														const updatedSettings = additionalSettings.map((setting, i) =>
															i === index ? { ...setting, value: value } : setting
														);
														setAdditionalSettings(updatedSettings);
													}}
													placeholder={__('Enter value', 'linear')}
													style={{ flex: '1' }}
												/>
												<Button
													isDestructive
													onClick={() => {
														setAdditionalSettings((prevSettings) => 
															prevSettings.filter((_, i) => i !== index)
														);
													}}
												>
													{__('Remove', 'linear')}
												</Button>
											</div>
										))}
									</div>
								}

								{/* Add new item button */}
								<Button
									variant="primary"
									onClick={() => {
										const newItem = {
											key: '', 
											value: '' 
										};
										setAdditionalSettings([...(additionalSettings || []), newItem]);
									}}
									style={{ marginTop: '10px' }}
								>
									{__('Add additional setting', 'linear')}
								</Button>
							</div>
						</blockquote>
					</PanelRow>
				</PanelBody>
				{ /*
				<PanelBody title={__('Setup', 'linear')}>
					<PanelRow>
						<h2>TODO Possible generator</h2>
					</PanelRow>
				</PanelBody>
				*/ }
			</Panel>
			<SaveButton
				onClick={ handleSaveSettings }
				disabled={
					! validateUrl( appUrl ) ||
					! validateUrl( dataUrl ) ||
					! validateClientSecret( clientSecret ) ||
					! Object.keys( dynamicParentPages ).length
				}
			/>
		</>
	);
};

export { SettingsPage };
